import { Vector3 } from 'three'

export abstract class Helper {
  public static calculateTriangle(
    a: number,
    alpha: number,
    gamma: number
  ): { b: number; c: number; beta: number } {
    // Wandle die Winkel alpha und gamma in Radian um
    alpha = (alpha * Math.PI) / 180
    gamma = (gamma * Math.PI) / 180

    // Berechne den Winkel beta des Dreiecks
    let beta = Math.PI - alpha - gamma

    // Berechne die Länge der Seite b des Dreiecks mit dem Sinussatz
    let b = (a * Math.sin(alpha)) / Math.sin(beta)

    // Berechne die Länge der Seite c des Dreiecks mit dem Sinussatz
    let c = (a * Math.sin(gamma)) / Math.sin(beta)

    // Gib das Ergebnis als Objekt mit den Seitenlängen und Winkeln zurück
    return { b: b, c: c, beta: (beta * 180) / Math.PI }
  }

  public static calculateTriangleSideLength(
    angle1: number,
    angle2: number,
    b: number
  ) {
    var angle3 = 180 - angle1 - angle2
    var c =
      (b / Math.sin((angle3 * Math.PI) / 180)) *
      Math.sin((angle2 * Math.PI) / 180)
    var a =
      (b / Math.sin((angle3 * Math.PI) / 180)) *
      Math.sin((angle1 * Math.PI) / 180)
    return {
      a: a,
      b: b,
      c: c,
    }
  }

  public static calculateAngleBeta(a: number, b: number): number {
    // Verhindern, dass b gleich 0 ist, um Division durch 0 zu vermeiden
    if (b === 0) {
      throw new Error('b darf nicht 0 sein.')
    }

    // Berechne den Tangens von β
    const tanBeta = a / b

    // Berechne den Winkel β in Radiant
    const angleBetaRad = Math.atan(tanBeta)

    // Konvertiere den Winkel β von Radiant in Grad
    const angleBetaDeg = angleBetaRad * (180 / Math.PI)

    return angleBetaDeg
  }

  public static calculateSideLengthC(
    a: number,
    alphaDeg: number,
    b: number
  ): number {
    // Konvertiere den Winkel α von Grad in Radiant
    const alphaRad = alphaDeg * (Math.PI / 180)

    // Berechne den Kosinus von α
    const cosAlpha = Math.cos(alphaRad)

    // Berechne c^2
    const cSquared = a * a + b * b - 2 * a * b * cosAlpha

    // Berechne c durch die Quadratwurzel
    const c = Math.sqrt(cSquared)

    return c
  }

  /**
   * Converts positive angle values to radiant value
   * @returns The converted radiant
   */
  public static convertAngleToRadiant(angle: number): number {
    return ((angle % 360) * Math.PI) / 180
  }

  public static amountOfBoards(broad: number, spaceBetween: number): number {
    return Math.ceil(spaceBetween / broad)
  }

  public static stringToNumbers(input: string): [number, number] {
    if (!input || typeof input !== 'string') return [0, 0]

    const regex = /([\d.]+)x([\d.]+)/
    const match = input?.match(regex)
    if (match) {
      const num1 = parseFloat(match[1])
      const num2 = parseFloat(match[2])
      return [num1, num2]
    } else {
      return [0, 0]
    }
  }

  public static stringToV3(input: string): [number, number, number] {
    if (!input || typeof input !== 'string') return [0, 0, 0]

    // Updated regex to handle negative and decimal numbers
    const regex = /-?\d+(\.\d+)?/g
    const match = input?.match(regex)
    if (match) {
      const num1 = parseFloat(match[0])
      const num2 = parseFloat(match[1])
      const num3 = parseFloat(match[2])
      return [num1, num2, num3]
    } else {
      return [0, 0, 0]
    }
  }

  public static setWindowsSideTotal(
    log: number,
    depth: number,
    attached: boolean,
    windowsSizeMax: number
  ): number {
    const sum = Math.ceil((depth - (attached ? log : log * 2)) / windowsSizeMax)
    return sum
  }
}
